import { useState, useMemo, memo } from 'react';
import { AnimatePresence } from 'motion/react';
import { Container } from '@components/ui/Container';
import { Section } from '@components/ui/Section';
import { EventCard } from '@components/ui/EventCard';
import { FadeIn } from '@components/motion/FadeIn';
import { ScrollReveal } from '@components/motion/ScrollReveal';
import { Eyebrow, H1, H2, Lead, GradientText, SectionHeader } from '@components/ui/Typography';
import { events } from '@data/events';
import type { EventCategory } from '../../types/event';
import {
  HeroSection,
  HeroGrid,
  HeroGlow,
  HeroContent,
  FiltersSection,
  FiltersRow,
  FilterLabel,
  FilterButton,
  EventsGrid,
  EventCardWrapper,
  EmptyState,
  EmptyIcon,
  EmptyText,
} from './styles';
import { clients } from '@/data/clients';
import { LogoCarousel } from '@/components';
// import { PageTransition } from '@/components';

/* ── Filtros disponíveis ──────────────────────────────────── */

type FilterValue = 'todos' | EventCategory;

const filters: { label: string; value: FilterValue }[] = [
  { label: 'Todos', value: 'todos' },
  { label: 'Festival', value: 'festival' },
  { label: 'Gastronomia', value: 'gastronomico' },
  { label: 'Varejo', value: 'varejo' },
  { label: 'Rural', value: 'rural' },
  { label: 'Exposição', value: 'exposicao' },
];

/* ── Ordenação: jan → dez ────────────────────────────────── */

const sortedEvents = [...events].sort((a, b) => {
  // Featured vem antes dos não featured
  if (a.featured && !b.featured) return -1;
  if (!a.featured && b.featured) return 1;
  // Dentro do mesmo grupo, ordena por data
  return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
});

/* ═══════════════════════════════════════════════════════════
   PÁGINA EVENTOS
══════════════════════════════════════════════════════════════ */

const Events: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<FilterValue>('todos');

  const filtered = useMemo(() => {
    if (activeFilter === 'todos') return sortedEvents;
    return sortedEvents.filter((e) => e.category === activeFilter);
  }, [activeFilter]);

  return (
    <>
      <title>Eventos — Teia de Eventos</title>
      <meta
        name="description"
        content="Conheça todos os eventos da Teia de Eventos: FEPRO, Nossa Feira, Festa do Morango com Chocolate, Festa da Flor, Festival de Comida de Boteco e muito mais."
      />

      {/* HERO */}
      <HeroSection>
        <HeroGrid />
        <HeroGlow />
        <Container>
          <HeroContent>
            <FadeIn direction="up" delay={0.1}>
              <Eyebrow>Nosso calendário</Eyebrow>
            </FadeIn>
            <FadeIn direction="up" delay={0.25}>
              <H1 $inverse>
                Eventos que movem <GradientText>Nova Friburgo.</GradientText>
              </H1>
            </FadeIn>
            <FadeIn direction="up" delay={0.4}>
              <Lead $inverse>
                Da maior feira de promoção da Região Serrana às festas que celebram nossa
                gastronomia e cultura — cada evento é planejado para superar expectativas.
              </Lead>
            </FadeIn>
          </HeroContent>
        </Container>
      </HeroSection>

      {/* FILTROS + GRID */}
      <Section $bg="secondary" $spacing="none">
        <Container>
          {/* Filtros */}
          <ScrollReveal direction="up" threshold={0.1}>
            <FiltersSection>
              <FiltersRow>
                <FilterLabel>Filtrar por</FilterLabel>
                {filters.map((f) => (
                  <FilterButton
                    key={f.value}
                    $active={activeFilter === f.value}
                    onClick={() => setActiveFilter(f.value)}
                    whileTap={{ scale: 0.95 }}
                  >
                    {f.label}
                  </FilterButton>
                ))}
              </FiltersRow>
            </FiltersSection>
          </ScrollReveal>

          {/* Grid */}
          <EventsGrid>
            <AnimatePresence mode="popLayout">
              {filtered.length > 0 ? (
                filtered.map((event, i) => (
                  <EventCardWrapper
                    key={event.slug}
                    layout
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{
                      duration: 0.4,
                      ease: [0.16, 1, 0.3, 1],
                      delay: i * 0.05,
                    }}
                  >
                    <EventCard event={event} />
                  </EventCardWrapper>
                ))
              ) : (
                <EmptyState
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <EmptyIcon>🗓️</EmptyIcon>
                  <EmptyText>Nenhum evento nesta categoria por enquanto.</EmptyText>
                </EmptyState>
              )}
            </AnimatePresence>
          </EventsGrid>
        </Container>
      </Section>

      {/* SEÇÃO INFERIOR */}
      <Section $bg="primary" $spacing="lg">
        <Container>
          <ScrollReveal>
            <SectionHeader $centered>
              <Eyebrow>Quer participar?</Eyebrow>
              <H2 style={{ textAlign: 'center' }}>
                Seja um <GradientText>expositor</GradientText>
              </H2>
              <Lead style={{ textAlign: 'center', margin: '0 auto' }}>
                Garanta seu espaço em um dos nossos eventos e coloque sua marca na frente de
                milhares de visitantes qualificados.
              </Lead>
            </SectionHeader>
          </ScrollReveal>
        </Container>
      </Section>
      {clients.length > 0 && (
        <Container>
          <ScrollReveal direction="up">
            <LogoCarousel clients={clients} />
          </ScrollReveal>
        </Container>
      )}
    </>
  );
};

export default memo(Events);
